/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useColorMode } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useSuperAdminPermissions } from "../../../hooks/useSuperAdminPermissions";
import {
  FiHome,
  FiShoppingBag,
  FiDollarSign,
  FiBarChart2,
  FiBell,
  FiTruck,
  FiClipboard,
  FiUsers,
  FiChevronRight,
  FiX,
} from "react-icons/fi";

export default function Sidebar({ isOpen, onClose, isMobile, isAlwaysVisible }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { colorMode } = useColorMode();
  const isArabic = i18n.language === "ar";
  const isDark = colorMode === "dark";

  // 👑 permissions الحقيقية
  const perms = useSuperAdminPermissions();

  const allMenuItems = useMemo(() => [
    {
      key: "dashboard",
      label: t("admin.dashboard"),
      icon: <FiHome />,
      to: "/admin",
      permission: "viewAdminDashboard",
    },
    {
      key: "stores",
      label: t("admin.stores"),
      icon: <FiShoppingBag />,
      to: "/admin/stores",
      permission: "viewAdminStores",
    },
    {
      key: "payments",
      label: t("admin.payments"),
      icon: <FiDollarSign />,
      to: "/admin/payments",
      permission: "viewAdminPayments" || "viewAdminPlansSubscriptions",
    },
    {
      key: "reports",
      label: t("admin.reports"),
      icon: <FiBarChart2 />,
      permission: "viewAdminReports",
      sub: [
        {
          key: "revenues",
          label: t("admin.reports.revenues"),
          to: "/admin/revenues",
          permission: "viewAdminReportsRevenues",
        },
        {
          key: "plans",
          label: t("admin.reports.plans"),
          to: "/admin/plans",
          permission: "viewAdminReportsPlans",
        },
        {
          key: "growth",
          label: t("admin.reports.growth"),
          to: "/admin/growth",
          permission: "viewAdminReportsGrowth",
        },
        {
          key: "paymentsReports",
          label: t("admin.reports.paymentsReports"),
          to: "/admin/paymentsReports",
          permission: "viewAdminReportsPayments",
        },
      ],
    },
    {
      key: "notifications",
      label: t("admin.notifications"),
      icon: <FiBell />,
      to: "/admin/notifications",
      permission: "viewAdminNotifications",
    },
    {
      key: "shipping",
      label: t("admin.shipping"),
      icon: <FiTruck />,
      to: "/admin/shipping",
      permission: "viewAdminShipping",
    },
    {
      key: "plansPage",
      label: t("admin.plans"),
      icon: <FiClipboard />,
      to: "/admin/plans",
      permission: "viewAdminPlansPage",
    },
    {
      key: "employees",
      label: t("admin.employees"),
      icon: <FiUsers />,
      to: "/admin/employees",
      permission: "viewAdminEmployees",
    },
  ], [t]);

  const [openMenu, setOpenMenu] = useState(null);

  // Auto-open menu based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    const parent = allMenuItems.find(item =>
      item.sub && item.sub.some(sub => sub.to === currentPath)
    );
    if (parent) {
      setOpenMenu(parent.key);
    }
  }, [location.pathname, allMenuItems]);

  useEffect(() => {
    document.documentElement.dir = isArabic ? "rtl" : "ltr";
  }, [isArabic]);

  // 📌 فلترة صلاحيات ال Sidebar
  const menu = useMemo(() => {
    return allMenuItems.filter((item) => {
      if (!item.permission) return true;

      // Reports submenu
      if (item.key === "reports" && item.sub) {
        const hasAny = item.sub.some((sub) => perms[sub.permission]);
        if (!hasAny) return false;

        return true;
      }

      return perms[item.permission];
    }).map(item => {
      if (item.key === "reports" && item.sub) {
        return {
          ...item,
          sub: item.sub.filter((sub) => perms[sub.permission])
        };
      }
      return item;
    });
  }, [allMenuItems, perms]);

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) onClose();
  };

  return (
    <aside
      dir={isArabic ? "rtl" : "ltr"}
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "fixed",
        top: 0,
        left: isArabic ? "auto" : 0,
        right: isArabic ? 0 : "auto",
        width: "300px",
        zIndex: isAlwaysVisible ? 40 : 1002,
        boxShadow: isAlwaysVisible ? "none" : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        transition: "transform 0.3s ease-in-out",
        transform: isMobile
          ? (isOpen ? "translateX(0)" : isArabic ? "translateX(100%)" : "translateX(-100%)")
          : "translateX(0)",
        backgroundColor: isDark ? "#0F172A" : "white",
        borderRight: isAlwaysVisible ? (isArabic ? "none" : (isDark ? "1px solid #1E293B" : "1px solid #e5e7eb")) : "none",
        borderLeft: isAlwaysVisible ? (isArabic ? (isDark ? "1px solid #1E293B" : "1px solid #e5e7eb") : "none") : "none",
      }}
      className={isOpen ? "open" : "closed"}
    >
      {/* العنوان */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "64px",
          minHeight: "64px",
          borderBottom: isDark ? "1px solid #1E293B" : "1px solid rgba(0, 0, 0, 0.08)",
          position: "relative",
        }}
      >
        {isMobile && (
          <FiX
            onClick={onClose}
            style={{
              position: "absolute",
              [isArabic ? "right" : "left"]: "16px",
              cursor: "pointer",
              fontSize: "1.5rem",
              opacity: 0.8,
              transition: "all 0.25s ease",
              color: isDark ? "#FFD700" : "#374151",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "1";
              e.currentTarget.style.transform = "scale(1.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "0.8";
              e.currentTarget.style.transform = "scale(1)";
            }}
          />
        )}
        <h1
          style={{
            fontWeight: 800,
            color: isDark ? "#FFD700" : "#7400BF",
            letterSpacing: "0.025em",
            fontSize: "1.125rem",
          }}
        >
          {isArabic ? "مدار" : "Madar"}
        </h1>
      </div>

      {/* القائمة */}
      <nav
        style={{
          flex: 1,
          padding: "16px",
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {menu.map((item) => {
            const hasSub = !!item.sub;
            const isOpenMenu = openMenu === item.key;
            const isSubActive = hasSub && item.sub.some(sub => location.pathname === sub.to);
            const isActive = (item.to && location.pathname === item.to) || isSubActive;

            return (
              <div key={item.key}>
                <button
                  onClick={() => {
                    if (hasSub) {
                      setOpenMenu(openMenu === item.key ? null : item.key);
                    } else if (item.to) {
                      handleNavigate(item.to);
                    }
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 12px",
                    borderRadius: "12px",
                    transition: "all 0.2s ease",
                    fontSize: "0.875rem",
                    fontWeight: isActive ? 700 : 500,
                    backgroundColor: isActive ? (isDark ? "rgba(255, 215, 0, 0.15)" : "rgba(116, 0, 191, 0.1)") : "transparent",
                    border: "none",
                    cursor: "pointer",
                    color: isActive
                      ? (isDark ? "#FFD700" : "#7400BF")
                      : (isDark ? "#FFD700" : "#374151"),
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = isDark ? "rgba(255, 215, 0, 0.1)" : "rgba(116, 0, 191, 0.1)";
                      e.currentTarget.style.color = isDark ? "#FFD700" : "#7400BF";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = isDark ? "#FFD700" : "#374151";
                    }
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        width: "36px",
                        height: "36px",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "8px",
                        backgroundColor: isActive
                          ? (isDark ? "rgba(255, 215, 0, 0.2)" : "rgba(116, 0, 191, 0.2)")
                          : (isDark ? "rgba(255, 215, 0, 0.1)" : "rgba(116, 0, 191, 0.1)"),
                        color: isDark ? "#FFD700" : "#7400BF",
                      }}
                    >
                      {item.icon}
                    </div>
                    <span style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: "1.2",
                      fontWeight: isActive ? 700 : 500,
                    }}>{item.label}</span>
                  </div>
                  {hasSub && (
                    <span
                      style={{
                        transition: "transform 0.3s ease",
                        transform: isOpenMenu ? "rotate(90deg)" : "rotate(0deg)",
                        color: isDark ? "#FFD700" : "#374151",
                      }}
                    >
                      <FiChevronRight />
                    </span>
                  )}
                </button>

                {hasSub && isOpenMenu && (
                  <div
                    style={{
                      marginTop: "4px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      borderLeft: isArabic ? "none" : `2px solid ${isDark ? "rgba(255, 215, 0, 0.3)" : "rgba(116, 0, 191, 0.3)"}`,
                      borderRight: isArabic ? `2px solid ${isDark ? "rgba(255, 215, 0, 0.3)" : "rgba(116, 0, 191, 0.3)"}` : "none",
                      paddingLeft: isArabic ? 0 : "24px",
                      paddingRight: isArabic ? "24px" : 0,
                    }}
                  >
                    {item.sub.map((sub) => {
                      const isSubActiveNow = location.pathname === sub.to;
                      return (
                        <NavLink
                          key={sub.key}
                          to={sub.to}
                          onClick={() => handleNavigate(sub.to)}
                          style={{
                            display: "block",
                            padding: "6px 8px",
                            borderRadius: "6px",
                            fontSize: "0.875rem",
                            transition: "all 0.2s ease",
                            textDecoration: "none",
                            color: isSubActiveNow
                              ? (isDark ? "#FFD700" : "#7400BF")
                              : (isDark ? "#FFD700" : "#64748b"),
                            fontWeight: isSubActiveNow ? 700 : 400,
                            backgroundColor: isSubActiveNow
                              ? (isDark ? "rgba(255, 215, 0, 0.15)" : "rgba(116, 0, 191, 0.1)")
                              : "transparent",
                          }}
                          onMouseEnter={(e) => {
                            if (!isSubActiveNow) {
                              e.currentTarget.style.color = isDark ? "#FFD700" : "#7400BF";
                              e.currentTarget.style.backgroundColor = isDark ? "rgba(255, 215, 0, 0.1)" : "rgba(116, 0, 191, 0.05)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSubActiveNow) {
                              e.currentTarget.style.color = isDark ? "#FFD700" : "#64748b";
                              e.currentTarget.style.backgroundColor = "transparent";
                            }
                          }}
                        >
                          {sub.label}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>
    </aside>
  );
}
